import { Dropdown, IDropdownOption, ResponsiveMode, TextField, Toggle } from "@fluentui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import useApi from "system/hooks/use-api";
import usePageTitle from "system/hooks/use-page-title";
import fluentUiClasses, {
  fluentUiControlStyles,
  fluentUiDarkTheme,
  fluentUiDropdownStyle,
} from "system/styles/fluent-ui-classes";
import { IPackageCustomerSummary, IPackageInfo } from "system/types/interfaces/IPackageInfo";
import ActionButtons from "components/ui/ActionButtons";
import ExportToCsvButton from "components/ui/ExportToCsvButton";
import ConnectionErrorPage from "../system/ConnectionErrorPage";
import PackageList from "./PackageList";
import { toDate } from "system/libraries/utilities";

const GLOBAL_OPTION_KEY = "__global__";

const GlobalAdminPackagesView = () => {
  const api = useApi();

  usePageTitle({ title: "Packages", subTitle: "" });

  const [customers, setCustomers] = useState<IPackageCustomerSummary[]>([]);
  const [showAllGlobal, setShowAllGlobal] = useState<boolean>(true);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([GLOBAL_OPTION_KEY]);
  const [packages, setPackages] = useState<IPackageInfo[]>([]);
  const [search, setSearch] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  const loadCustomers = useCallback(async () => {
    try {
      const result = await api.packages.getCustomers();
      setCustomers(result?.items ?? []);
    } catch {
      setHasError(true);
    }
  }, [api.packages]);

  const loadPackages = useCallback(async () => {
    setIsLoading(true);
    setHasError(false);

    try {
      if (showAllGlobal) {
        const result = await api.packages.getGlobal();
        setPackages(result?.items ?? []);
        return;
      }

      const hasGlobal = selectedKeys.includes(GLOBAL_OPTION_KEY);
      const customerKeys = selectedKeys.filter((k) => k !== GLOBAL_OPTION_KEY);

      const requests: Promise<{ items: IPackageInfo[] | null }>[] = [];
      if (hasGlobal) {
        requests.push(api.packages.getGlobal());
      }
      if (customerKeys.length > 0) {
        requests.push(api.packages.getSpecifics(customerKeys));
      }

      if (requests.length === 0) {
        setPackages([]);
        return;
      }

      const results = await Promise.all(requests);
      setPackages(results.flatMap((r) => r?.items ?? []));
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [api.packages, showAllGlobal, selectedKeys]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  const onToggleGlobal = useCallback((_: unknown, checked?: boolean) => {
    setShowAllGlobal(!!checked);
    if (!checked) {
      setSelectedKeys([GLOBAL_OPTION_KEY]);
    }
  }, []);

  const onDropdownChange = useCallback(
    (option: IDropdownOption<string> | undefined) => {
      if (typeof option?.key !== "string") return;
      if (option.selected && selectedKeys.indexOf(option.key) === -1) {
        setSelectedKeys([...selectedKeys, option.key]);
      } else if (!option.selected) {
        setSelectedKeys(selectedKeys.filter((k) => k !== option.key));
      }
    },
    [selectedKeys]
  );

  const filteredPackages = useMemo<IPackageInfo[]>(() => {
    const term = search.trim().toLowerCase();
    if (!term) return packages;
    return packages.filter((p) => p.name.toLowerCase().includes(term));
  }, [packages, search]);

  const showSourceColumn = useMemo<boolean>(() => {
    if (showAllGlobal) return false;
    const customerCount = selectedKeys.filter((k) => k !== GLOBAL_OPTION_KEY).length;
    return customerCount > 0;
  }, [showAllGlobal, selectedKeys]);

  const exportData = useMemo(() => {
    const fileName = `Aiden Packages - ${filteredPackages.length} packages - ${toDate(new Date().toString())}`;
    return {
      fileName,
      source: filteredPackages.map((p) => ({
        name: p.name,
        source: p.isCustomerSpecific ? p.customerShortname ?? "" : "Global",
      })),
    };
  }, [filteredPackages]);

  const refreshHandler = useCallback(() => {
    loadCustomers();
    loadPackages();
  }, [loadCustomers, loadPackages]);

  const dropdownOptions = useMemo<IDropdownOption<string>[]>(
    () => [
      { key: GLOBAL_OPTION_KEY, text: "All Global Packages" },
      ...customers.map((c) => ({
        key: c.shortname,
        text: `${c.shortname} (${c.specificsCount})`,
      })),
    ],
    [customers]
  );

  return (
    <div style={{ color: "var(--text-color)" }}>
      <ActionButtons onRefresh={refreshHandler}>
        <ExportToCsvButton items={[exportData]} />
      </ActionButtons>
      {hasError ? (
        <ConnectionErrorPage />
      ) : (
        <div className="card-body">
          <div className={fluentUiClasses.controlWrapper}>
            <TextField
              label="Search Packages:"
              value={search}
              onChange={(_, value?: string) => setSearch(value ?? "")}
              styles={fluentUiControlStyles}
              disabled={isLoading}
              theme={fluentUiDarkTheme}
            />
            <Toggle
              label="Show Packages"
              checked={showAllGlobal}
              onChange={onToggleGlobal}
              onText="All Packages"
              offText="Company specific Packages"
              styles={fluentUiControlStyles}
              disabled={isLoading}
              theme={fluentUiDarkTheme}
            />
            {!showAllGlobal && (
              <Dropdown
                label="Sources"
                multiSelect={true}
                dropdownWidth={300}
                selectedKeys={selectedKeys}
                onChange={(_, option: IDropdownOption<string> | undefined) => {
                  onDropdownChange(option);
                }}
                responsiveMode={ResponsiveMode.xxxLarge}
                styles={fluentUiDropdownStyle}
                options={dropdownOptions}
                theme={fluentUiDarkTheme}
              />
            )}
          </div>
          <PackageList items={filteredPackages} isLoading={isLoading} showSourceColumn={showSourceColumn} />
        </div>
      )}
    </div>
  );
};

export default GlobalAdminPackagesView;
